import {
  type QuizBankFacetsRes,
  type QuizBankHintRes,
  type QuizBankItemRes,
  type QuizBankUnitFacetRes,
} from '@/api/quizBank';

import { QUIZ_BANK_QUIZ_TYPE_KEYS } from '../consts';
import {
  type QuizBankFilterOption,
  type QuizBankFilterState,
  type QuizBankProblem,
  type QuizBankQuizType,
  type QuizBankTranslate,
} from '../types';

import { hasContent, toStem } from './quizBankContent';

/**
 * 서버 응답 → 뷰 모델. 필터·카운트·정렬·검색은 전부 서버(facets + 커서)가 하고,
 * 여기서는 모양만 옮긴다.
 */

/** 초등 학년 범위. 이 밖의 코드는 라벨을 지어내지 않고 코드를 그대로 보여준다. */
const FIRST_GRADE = 1;
const LAST_GRADE = 6;

const SUPPORTED_TYPES = new Set<string>(['multiple', 'subjective', 'ox']);

const isSupportedType = (type: string): type is QuizBankQuizType =>
  SUPPORTED_TYPES.has(type);

/**
 * 학년 코드(`'03'`) → 라벨.
 *
 * 학년마다 키를 두지 않고 숫자를 끼워 넣는다 — 6개 키가 한 문형의 변주일 뿐이라
 * 번역이 갈라질 이유가 없다. 아는 학년이 아니면 코드를 그대로 보여준다.
 */
export const toGradeLabel = (t: QuizBankTranslate, gradeCode: string) => {
  const grade = Number(gradeCode);
  if (!Number.isInteger(grade) || grade < FIRST_GRADE || grade > LAST_GRADE) {
    return gradeCode;
  }

  return t('quizBank.filter.gradeOption', { grade });
};

/** 폼 스키마(`QuizHintSchema`)가 받는 세 유형만 통과시킨다. */
const HINT_TYPES = new Set(['text', 'image', 'youtube']);

const toQuizBankHint = (hint: QuizBankHintRes | null | undefined) => {
  const type = hint?.type;
  const value = hint?.value;
  if (!type || !value || !HINT_TYPES.has(type)) return undefined;
  return { type: type as 'text' | 'image' | 'youtube', value };
};

/**
 * `answerList` 는 인덱스 문자열(`["1","3"]`)이다. 하나라도 인덱스가 아니거나
 * 보기 범위를 벗어나면 `null` — `.map(Number)` 가 `''` 를 `0` 으로 만들어
 * 빈 정답이 1번 보기로 둔갑하는 것을 막는다.
 */
const toAnswerIndexes = (
  answerList: string[] | undefined,
  choiceCount: number,
): number[] | null => {
  const rawAnswers = answerList ?? [];
  if (rawAnswers.length === 0) return null;
  if (!rawAnswers.every(answer => /^\d+$/.test(answer.trim()))) return null;

  const indexes = rawAnswers.map(answer => Number(answer.trim()));
  return indexes.every(index => index < choiceCount) ? indexes : null;
};

/**
 * 문항 하나 → 카드 모델. 폼 검증(`QuizMultipleDataSchema` 등)을 통과할 수 없는
 * 문항은 `null` 로 걸러 목록에서 뺀다 — 담은 뒤 게시 단계에서 막히는 것보다 낫다.
 */
export const toQuizBankProblem = (
  item: QuizBankItemRes,
): QuizBankProblem | null => {
  if (!isSupportedType(item.type)) return null;

  const standard = {
    code: item.standard.code,
    description: item.standard.description ?? '',
    unitId: item.unitId ?? undefined,
    unitName: item.unitName ?? undefined,
  };

  const author = {
    name: item.author.username,
    avatarData: item.author.avatarData ?? null,
  };

  const base = {
    id: item.id,
    standard,
    author,
    gradeId: item.grade,
    // 표본 미달이면 null. `0`(정답률 0%)과 섞지 않는다.
    answerRate: item.correctRate ?? undefined,
    imageUrl: item.imageUrl ?? undefined,
    hint: toQuizBankHint(item.hint),
    // 구서버·구캐시 응답에는 없다 — 그러면 칠하지 않는다(클라이언트가 다시 매칭하지 않는다)
    hitTerms: item.hitTerms ?? [],
  };

  if (item.type === 'multiple') {
    const data = item.multipleData;
    if (!data) return null;

    const stem = toStem(data);
    if (!stem) return null;

    const choices = data.choiceListV2 ?? [];
    const answerIndexes = toAnswerIndexes(data.answerList, choices.length);
    if (!answerIndexes) return null;

    const options = choices.map(choice => ({
      text: choice.text,
      richText: choice.richText ?? undefined,
      imageUrl: choice.imageUrl ?? undefined,
    }));

    // 텍스트도 이미지도 없는 보기가 있으면 스키마가 문항 전체를 거부한다.
    if (
      options.some(
        option =>
          !option.imageUrl &&
          !hasContent(option.richText) &&
          !hasContent(option.text),
      )
    ) {
      return null;
    }

    return {
      ...base,
      type: 'multiple',
      stem,
      options,
      answerIndexes,
    };
  }

  if (item.type === 'subjective') {
    const data = item.subjectiveData;
    if (!data) return null;

    const stem = toStem(data);
    if (!stem) return null;

    const answerTexts = (data.answerList ?? []).filter(
      answer => answer.trim().length > 0,
    );
    if (answerTexts.length === 0) return null;

    return {
      ...base,
      type: 'subjective',
      stem,
      answerTexts,
    };
  }

  const data = item.oxData;
  if (!data) return null;

  const stem = toStem(data);
  if (!stem) return null;

  // 정확히 o/x 가 아니면 정답을 추측하지 않는다.
  const oxAnswer = data.answer?.trim().toLowerCase();
  if (oxAnswer !== 'o' && oxAnswer !== 'x') return null;

  return {
    ...base,
    type: 'ox',
    stem,
    oxAnswer,
  };
};

export const toQuizBankProblems = (items: QuizBankItemRes[]) =>
  items
    .map(toQuizBankProblem)
    .filter((problem): problem is QuizBankProblem => Boolean(problem));

interface FacetViewModel {
  gradeOptions: QuizBankFilterOption[];
  unitOptions: QuizBankFilterOption[];
  typeOptions: QuizBankFilterOption[];
}

/**
 * facets → 좌측 패널 옵션. 0건 처리는 서버가 이미 했다(학년·유형은 0건도 내려오고,
 * 단원은 0건이면 빠진다). 여기서는 선택 상태만 얹는다.
 */
export const toQuizBankFacetViewModel = (
  t: QuizBankTranslate,
  facets: QuizBankFacetsRes | null | undefined,
  filter: QuizBankFilterState,
): FacetViewModel => {
  if (!facets) {
    return { gradeOptions: [], unitOptions: [], typeOptions: [] };
  }

  return {
    gradeOptions: facets.grades.map(grade => ({
      id: grade.code,
      label: toGradeLabel(t, grade.code),
      count: grade.count,
      checked: filter.gradeId === grade.code,
    })),

    typeOptions: facets.types
      .filter(type => isSupportedType(type.code))
      .map(type => ({
        id: type.code,
        label: t(QUIZ_BANK_QUIZ_TYPE_KEYS[type.code as QuizBankQuizType]),
        count: type.count,
        checked: filter.types.includes(type.code as QuizBankQuizType),
      })),

    unitOptions: toUnitOptions(facets.units, filter),
  };
};

export const toUnitGroupLabel = (
  t: QuizBankTranslate,
  grade: number,
  semester: number,
) => t('quizBank.filter.unitGroup', { grade, semester });

/** 단원 라벨은 단원명만 쓴다. 목록 순서가 이미 교과서 차례라 학기·순번은 뺀다. */
export const toUnitLabel = (unit: {
  unitId: string;
  /** 미이행 단원은 `null` — `unitId` 로 대신한다 */
  unitName?: string | null;
}): string => unit.unitName ?? unit.unitId;

/** facet 의 `grade` 는 숫자(`3`), 필터의 학년은 코드(`'03'`)다. */
const belongsToSelectedGrade = (unitGrade: number, gradeId: string | null) =>
  gradeId === null || unitGrade === Number(gradeId);

/**
 * 선택된 학년의 단원만 남긴다. 학년을 바꾼 직후 응답이 오기 전까지는 이전 학년의
 * facets(placeholder)가 남아 있어, 걸러 주지 않으면 다른 학년 단원이 잠깐 보인다.
 */
const toUnitOptions = (
  units: QuizBankUnitFacetRes[],
  filter: QuizBankFilterState,
): QuizBankFilterOption[] =>
  units
    .filter(unit => belongsToSelectedGrade(unit.grade, filter.gradeId))
    .map(unit => ({
      id: unit.unitId,
      label: toUnitLabel(unit),
      count: unit.count,
      checked: filter.unitIds.includes(unit.unitId),
    }));

/** 적용 중인 필터 → 칩 목록. 전체 학년(`null`)은 칩을 만들지 않는다. */
export const toQuizBankAppliedChips = (
  t: QuizBankTranslate,
  filter: QuizBankFilterState,
  facets: QuizBankFacetsRes | null | undefined,
) => {
  const unitsById = new Map(
    (facets?.units ?? []).map(unit => [unit.unitId, unit] as const),
  );

  return [
    ...(filter.gradeId
      ? [
          {
            axis: 'grade' as const,
            value: filter.gradeId,
            label: toGradeLabel(t, filter.gradeId),
          },
        ]
      : []),
    ...filter.unitIds.map(unitId => {
      const unit = unitsById.get(unitId);
      return {
        axis: 'unit' as const,
        value: unitId,
        label: unit?.unitName ?? unitId,
        // 같은 단원명이 여러 학기에 있을 수 있어 학기를 hover 로 푼다
        description: unit
          ? toUnitGroupLabel(t, unit.grade, unit.semester)
          : undefined,
      };
    }),
    ...filter.types.map(type => ({
      axis: 'type' as const,
      value: type,
      label: t(QUIZ_BANK_QUIZ_TYPE_KEYS[type]),
    })),
  ];
};
